import React, { useMemo, useState } from 'react'
import { Link, withRouter } from 'react-router-dom'
import T from 'prop-types'
import { connect } from 'react-redux'
import { Field, Form } from 'react-final-form'
import PhoneInput from 'react-phone-input-2'
import { injectIntl } from 'react-intl'
import { MenuItem, Tooltip } from '@material-ui/core'
import IconBack from '@material-ui/icons/ArrowBack'
import Button from '@material-ui/core/Button'
import cn from 'classnames'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import SidePopover from 'components/common/Popover/SidePopover'
import withPromise from 'helpers/withPromise'
import { userDeleteOwnAccount } from 'reducers/user/reducer'
import Loader from 'components/Loading/SmallLoader'
import {
  updatePassword,
  updatePhoto,
  updateProfile,
} from 'reducers/profile/reducer'
import getValidator, {
  VALIDATOR_EMAIL,
  VALIDATOR_IS_EMPTY,
  VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
} from 'validation'
import * as ROUTE from 'constants/appRouting'
import { Select } from 'components/common/FormInputs'
import { APP_LANGUAGES } from 'constants/index'
import { ReactComponent as AvatarPlaceholderIcon } from 'assets/img/avatar-placeholder.svg'
import { ReactComponent as EditIcon } from 'assets/img/edit-icon.svg'
import SignatureModal from 'components/common/SignatureModal/SignatureModal'
import { ReactComponent as MenuIconProfile } from 'assets/img/menu/menuIconProfile.svg'
import { ReactComponent as MenuIconBell } from 'assets/img/menu/menuIconBell.svg'
import messages from './translate'
import ChangeAvatarDialog from './ChangeAvatarDialog'
import PasswordDialog from './PasswordDialog'
import 'styles/app/profile/style.scss'
import Input from '../common/Form/Input'
import 'react-phone-input-2/lib/style.css'
import ConfirmDialog from '../common/Popup/ConfirmDialog'
import SignatureField from './SignatureField/SignatureField'

function ProfilePersonalInfo({
  intl: { formatMessage },
  history: { goBack, push },
  userData,
  updatePhoto,
  updateProfile,
  updatePassword,
  userDeleteOwnAccount,
  featureFlags,
  isSignatureSaving,
  isProfileFetching,
}) {
  const data = userData.toJS()
  const {
    email: prevEmail,
    name: prevName,
    notificationsEnabled: prevNotificationsEnabled,
    locale: prevLocale,
    phone: prevPhone,
    signatureUrl,
  } = data
  const [isOpenChangeAvatar, setIsOpenChangeAvatar] = useState(false)
  const [isOpenChangePass, setIsOpenChangePass] = useState(false)
  const [isOpenDeleteAccount, setIsOpenDeleteAccount] = useState(false)
  const [isOpenSignature, setIsOpenSignature] = useState(false)
  const [editNameButtonsDisplay, setEditNameButtonsDisplay] = useState(false)
  const langList = Object.values(APP_LANGUAGES)
  const langTitles = {
    english: 'English',
    spain: 'Spanish',
    thai: 'Thai',
    portugal: 'Portuguese',
    russian: 'Russian',
    ukrainian: 'Ukrainian',
    norwegian: 'Norwegian',
    polish: 'Polish',
  }

  const onSaveInfo = values => {
    const {
      email = null,
      name,
      notificationsEnabled,
      locale,
      phone,
      phoneCountryName,
      phoneCountryPrefix,
    } = values

    if (
      prevEmail !== email ||
      prevName !== name ||
      prevNotificationsEnabled !== notificationsEnabled ||
      prevLocale !== locale ||
      phone !== prevPhone
    ) {
      const isPhoneEmpty = phone === phoneCountryPrefix
      const payloadData = {
        email,
        name,
        locale,
        phone: isPhoneEmpty ? null : phone,
        phone_country_name: isPhoneEmpty ? null : phoneCountryName,
        phone_country_prefix: isPhoneEmpty ? null : phoneCountryPrefix,
        notifications_enabled: notificationsEnabled,
      }

      setEditNameButtonsDisplay(false)

      return withPromise(updateProfile, {
        data: payloadData,
        successMessage: formatMessage(messages.profileWasUpdated),
      }).then(errors => Promise.resolve(errors))
    }
  }

  const onChangePasswordSubmit = values =>
    withPromise(updatePassword, values).then(errors =>
      errors ? Promise.resolve(errors) : setIsOpenChangePass(false),
    )

  const toggleIsOpenSignature = () => setIsOpenSignature(!isOpenSignature)

  const isSignatureLoading = useMemo(
    () => isSignatureSaving || isProfileFetching,
    [isSignatureSaving, isProfileFetching],
  )

  const renderSignatureModal = () => (
    <SignatureModal
      isOpen={isOpenSignature}
      toggleIsOpen={toggleIsOpenSignature}
      isSignatureSaving={isSignatureLoading}
      confirmButtonText={formatMessage(messages.createButton)}
      title={`${formatMessage(messages.createButton)} ${formatMessage(
        messages.signature,
      ).toLowerCase()}`}
    />
  )

  return (
    <>
      <div className="profile-page">
        <aside className="profile-page-sidebar">
          <h2 className="profile-page-title">
            <IconBack
              onClick={() => {
                goBack()
              }}
            />
            {formatMessage(messages.settings)}
          </h2>
          <nav className="profile-page-nav">
            <div className="nav-block">
              <div className="nav-block-title">
                {formatMessage(messages.personalSettings)}
              </div>
              <Link to={ROUTE.PROFILE_PERSONAL_INFO} className="active">
                <MenuIconProfile />
                {formatMessage(messages.personalInfo)}
              </Link>
              <Link to={ROUTE.PROFILE_NOTIFICATIONS}>
                <MenuIconBell />
                {formatMessage(messages.myNotifications)}
              </Link>
            </div>
          </nav>
        </aside>
        <div className="profile-page-content">
          <h2 className="profile-page-title">
            <MenuIconProfile />
            {formatMessage(messages.personalInfo)}
          </h2>
          <Form
            onSubmit={onSaveInfo}
            initialValues={data}
            mutators={{
              setLocale: ([value], state, { changeValue }) => {
                changeValue(state, 'locale', () => value)
              },
              setPhone: ([value], state, { changeValue }) => {
                changeValue(state, 'phone', () => value)
              },
              setPhoneCountryName: ([value], state, { changeValue }) => {
                changeValue(state, 'phoneCountryName', () => value)
              },
              setPhoneCountryPrefix: ([value], state, { changeValue }) => {
                changeValue(state, 'phoneCountryPrefix', () => value)
              },
            }}
            render={({ handleSubmit, submitting, form: { mutators } }) => (
              <div className="profile-page-edit-section-holder with-avatar">
                <div className="profile-page-edit-section">
                  <div className="edit-avatar-section">
                    {data.pictureUrl ? (
                      <img
                        src={data.pictureUrl}
                        alt={formatMessage(messages.profileAvatar)}
                      />
                    ) : (
                      <AvatarPlaceholderIcon />
                    )}
                    <Tooltip title={formatMessage(messages.editAvatar)}>
                      <div
                        className="edit-avatar"
                        onClick={() => {
                          setIsOpenChangeAvatar(true)
                        }}>
                        <EditIcon />
                      </div>
                    </Tooltip>
                  </div>
                  <section
                    className={cn(
                      'profile-page-edit-section-block name-input',
                      {
                        'is-focused': editNameButtonsDisplay,
                      },
                    )}>
                    <Field
                      name="name"
                      type="text"
                      label={formatMessage(messages.name)}
                      disabled={submitting}
                      validate={getValidator([
                        VALIDATOR_IS_EMPTY,
                        VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
                      ])}
                      render={props => (
                        <>
                          <Input
                            {...props}
                            InputProps={{
                              onFocus: e => {
                                // eslint-disable-next-line no-param-reassign
                                e.currentTarget.selectionStart = 0
                                // eslint-disable-next-line no-param-reassign
                                e.currentTarget.selectionEnd =
                                  e.currentTarget.value.length
                                setEditNameButtonsDisplay(true)
                              },
                            }}
                          />
                          {editNameButtonsDisplay && (
                            <div className="buttons-holder">
                              <Button
                                className="profile-page-button"
                                onClick={() => {
                                  setEditNameButtonsDisplay(false)
                                }}>
                                {formatMessage(messages.cancel)}
                              </Button>
                              <Button
                                className="profile-page-button primary"
                                onClick={handleSubmit}>
                                {formatMessage(messages.save)}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    />
                  </section>
                  <section className="profile-page-edit-section-block">
                    <div className="profile-page-title-section">
                      <div className="title">
                        {formatMessage(messages.contacts)}
                      </div>
                    </div>
                    <div className="edit-contacts-section">
                      <Field
                        name="email"
                        type="email"
                        label={formatMessage(messages.email)}
                        disabled={submitting}
                        validate={getValidator([VALIDATOR_EMAIL])}
                        InputProps={{ onBlur: handleSubmit }}
                        component={Input}
                      />
                      <PhoneInput
                        value={data.phone}
                        specialLabel={formatMessage(messages.mobilePhone)}
                        country="us"
                        enableSearch
                        disableSearchIcon
                        disabled={submitting}
                        countryCodeEditable={false}
                        onChange={(value, { dialCode, name: countryName }) => {
                          mutators.setPhone(`+${value}`)
                          mutators.setPhoneCountryPrefix(`+${dialCode}`)
                          mutators.setPhoneCountryName(countryName)
                        }}
                        onBlur={handleSubmit}
                      />
                    </div>
                  </section>
                  <section className="profile-page-edit-section-block">
                    <div className="profile-page-title-section">
                      <div className="title">
                        {formatMessage(messages.password)}
                      </div>
                      <div
                        className="edit-password-button"
                        onClick={() => {
                          setIsOpenChangePass(true)
                        }}>
                        {formatMessage(messages.change)}
                      </div>
                    </div>
                  </section>
                  {featureFlags.DIGITAL_SIGNATURE && (
                    <section className="profile-page-edit-section-block lang-input">
                      <div className="profile-page-title-section">
                        <div className="title">
                          {formatMessage(messages.signature)}
                        </div>
                        {isSignatureLoading && <Loader />}
                        {!isSignatureLoading &&
                          (signatureUrl ? (
                            <SignatureField signatureUrl={signatureUrl} />
                          ) : (
                            <div
                              className="edit-password-button"
                              onClick={toggleIsOpenSignature}>
                              {formatMessage(messages.createButton)}
                            </div>
                          ))}
                      </div>
                    </section>
                  )}
                  <section className="profile-page-edit-section-block lang-input">
                    <div className="profile-page-title-section">
                      <div className="title">
                        {formatMessage(messages.language)}
                      </div>
                      <Field
                        value={data.locale}
                        name="locale"
                        type="select"
                        component={Select}
                        disabled={submitting}
                        validate={getValidator(VALIDATOR_IS_EMPTY)}
                        parse={value => {
                          mutators.setLocale(value)
                          handleSubmit()

                          return value
                        }}>
                        {langList.map(({ key, title }) => (
                          <MenuItem
                            key={key}
                            value={key}
                            className="lang-input-option">
                            {langTitles[title]}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="description-text">
                      {formatMessage(messages.localeExplanationText)}
                    </div>
                  </section>
                  <section className="profile-page-edit-section-block delete-account-section">
                    <div className="buttons-holder">
                      <Button
                        className="profile-page-button secondary"
                        onClick={() => {
                          setIsOpenDeleteAccount(true)
                        }}>
                        {formatMessage(messages.deleteAccount)}
                      </Button>
                    </div>
                  </section>
                </div>
              </div>
            )}
          />
        </div>
      </div>
      <ChangeAvatarDialog
        isOpenChangeAvatar={isOpenChangeAvatar}
        onCloseChangeAvatar={() => {
          setIsOpenChangeAvatar(false)
        }}
        updatePhoto={updatePhoto}
      />
      <PasswordDialog
        isOpenedModal={isOpenChangePass}
        onCloseModal={() => {
          setIsOpenChangePass(false)
        }}
        onSubmit={onChangePasswordSubmit}
      />
      <ConfirmDialog
        open={isOpenDeleteAccount}
        cancel={{
          color: 'default',
          variant: 'outlined',
          customText: formatMessage(messages.cancel),
        }}
        confirm={{
          color: 'scales-secondary',
          customText: formatMessage(messages.delete),
        }}
        onClose={() => {
          setIsOpenDeleteAccount(false)
        }}
        onConfirmAction={() => {
          userDeleteOwnAccount({
            redirect: push,
          })
        }}
        className="dialog-primary-custom"
        title={formatMessage(messages.deleteUser)}
        message={formatMessage(messages.deleteUserMessage)}
      />
      {ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
        <SidePopover
          open={isOpenSignature}
          close={toggleIsOpenSignature}
          className="modal-content"
          position="bottom">
          {renderSignatureModal()}
        </SidePopover>
      ) : (
        renderSignatureModal()
      )}
    </>
  )
}

ProfilePersonalInfo.propTypes = {
  userData: T.object.isRequired,
  featureFlags: T.object.isRequired,
}

export default connect(
  state => ({
    userData: state.getIn(['profile', 'profile']),
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
    isSignatureSaving: state.getIn(['profile', 'isSignatureSaving']),
    isProfileFetching: state.getIn(['profile', 'isProfileFetching']),
  }),
  {
    updateProfile,
    updatePhoto,
    updatePassword,
    userDeleteOwnAccount,
  },
)(withRouter(injectIntl(ProfilePersonalInfo)))
